import React from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { SecondaryButton, StickyModalActions } from '@/styles/blocks';

const RedText = ({ children }) => (
    <Grid
        item
        css={css`
            color: red;
        `}
    >
        {children}
    </Grid>
);

/**
 * The info line shown under the title after the modal loaded a newer version of the order.
 *
 * @param {object} props
 * @param {boolean} props.show True once the open-time re-sync loaded a newer version.
 * @param {() => void} props.onDismiss Hides the line.
 */
export const ResyncNotice = ({ show, onDismiss }) =>
    show ? (
        <div
            role="status"
            css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 16px;
                padding: 8px 24px;
                background: #e7f1ff;
                color: #004085;
            `}
        >
            <span>This order changed after the page loaded. You're editing the latest version.</span>
            <SecondaryButton onClick={onDismiss}>Dismiss</SecondaryButton>
        </div>
    ) : null;

/**
 * The red notices above the footer: order on hold for payment, payment already captured, and the
 * PC Miler load failure with its Retry button. Each shows only when its condition holds.
 *
 * @param {object} props
 * @param {object} props.order The order prop (saved values, not the draft).
 * @param {boolean} props.showAdminTab Whether the admin tab is visible to this user.
 * @param {Error} [props.pcMilerError] The PC Miler config load error, if any.
 * @param {() => Promise<unknown>} [props.onRetryPcMiler] Reloads the PC Miler config.
 */
const ModalBanners = ({ order, showAdminTab, pcMilerError, onRetryPcMiler }) => (
    <>
        {order.order_status === 'on_hold_awaiting_payment' && (
            <StickyModalActions border="true">
                <Grid container direction="row" justifyContent="center">
                    <RedText>
                        Order is on hold pending payment authorization from customer. You can continue to make edits and
                        the price will automatically adjust, but the order status cannot be updated until payment is
                        authorized.
                    </RedText>
                </Grid>
            </StickyModalActions>
        )}
        {showAdminTab && order.payment_status === 'CAPTURED' && order.source_form === 'CUSTOMER' && (
            <StickyModalActions border="true">
                <Grid container direction="row" justifyContent="center">
                    <RedText>
                        Payment has already been captured for this customer shipment form order. Further edits will not
                        result in price changing and could lead to a loss of revenue.
                    </RedText>
                </Grid>
            </StickyModalActions>
        )}
        {pcMilerError && (
            <StickyModalActions border="true">
                <Grid container direction="row" justifyContent="center" alignItems="center">
                    <RedText>
                        Couldn't load this customer's mileage settings, so saving would risk pricing the order off the
                        wrong mileage engine. Retry before saving.
                    </RedText>
                    <Grid item>
                        <SecondaryButton
                            onClick={() => {
                                onRetryPcMiler?.().catch((err) => console.error(err));
                            }}
                        >
                            Retry
                        </SecondaryButton>
                    </Grid>
                </Grid>
            </StickyModalActions>
        )}
    </>
);

export default ModalBanners;
